import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { Editor } from "@tiptap/core";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import {
  getSpeakerMarkdownFromEditor,
  speakerScriptExtensions,
} from "./speakerTiptap";

type BubbleProps = { editor: Editor };

function SpeakerScriptBubbleMenu({ editor }: BubbleProps) {
  const run = (fn: () => boolean) => () => {
    fn();
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 120, placement: "top" }}
      className="speaker-md-bubble"
    >
      <div className="speaker-md-bubble-inner" role="toolbar" aria-label="Formattazione discorso">
        <button
          type="button"
          className={editor.isActive("bold") ? "active" : ""}
          onClick={run(() => editor.chain().focus().toggleBold().run())}
          title="Grassetto (concetto chiave)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={editor.isActive("italic") ? "active" : ""}
          onClick={run(() => editor.chain().focus().toggleItalic().run())}
          title="Corsivo (enfasi vocale)"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={editor.isActive("highlight") ? "active" : ""}
          onClick={run(() => editor.chain().focus().toggleHighlight().run())}
          title="Evidenza"
        >
          ◆
        </button>
        <span className="speaker-md-bubble-sep" aria-hidden />
        <button
          type="button"
          className={editor.isActive("heading", { level: 3 }) ? "active" : ""}
          onClick={run(() => editor.chain().focus().toggleHeading({ level: 3 }).run())}
          title="Beat / sezione"
        >
          H3
        </button>
      </div>
    </BubbleMenu>
  );
}

type EditorProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
};

export type SpeakerScriptEditorHandle = {
  getMarkdown: () => string;
};

export const SpeakerScriptEditor = forwardRef<SpeakerScriptEditorHandle, EditorProps>(
  function SpeakerScriptEditor(
    {
      value,
      onChange,
      onBlur,
      placeholder = "Scrivi o genera il discorso per questa slide…",
    },
    ref,
  ) {
  const applyingRemote = useRef(false);
  const localEdit = useRef(false);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  const editor = useEditor(
    {
      extensions: speakerScriptExtensions(),
      content: value || "",
      editorProps: {
        attributes: {
          class: "speaker-md-editor__content",
          "data-placeholder": placeholder,
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (applyingRemote.current) return;
        localEdit.current = true;
        onChangeRef.current(getSpeakerMarkdownFromEditor(ed));
        window.setTimeout(() => {
          localEdit.current = false;
        }, 50);
      },
      onBlur: ({ editor: ed }) => {
        onBlurRef.current?.(getSpeakerMarkdownFromEditor(ed));
      },
    },
    [placeholder],
  );

  useImperativeHandle(
    ref,
    () => ({
      getMarkdown: () => (editor ? getSpeakerMarkdownFromEditor(editor) : value),
    }),
    [editor, value],
  );

  useEffect(() => {
    if (!editor) return;
    if (localEdit.current) return;
    const current = getSpeakerMarkdownFromEditor(editor);
    if (current === value) return;
    applyingRemote.current = true;
    editor.commands.setContent(value || "", false);
    applyingRemote.current = false;
  }, [editor, value]);

  return (
    <div className="speaker-md-editor">
      {editor ? <SpeakerScriptBubbleMenu editor={editor} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
  },
);

type ViewProps = {
  markdown: string;
  className?: string;
};

/** Read-only WYSIWYG view (teleprompter, preview). */
export function SpeakerScriptView({ markdown, className }: ViewProps) {
  const editor = useEditor(
    {
      extensions: speakerScriptExtensions(),
      content: markdown || "",
      editable: false,
      editorProps: {
        attributes: {
          class: "speaker-md-editor__content speaker-md-editor__content--readonly",
        },
      },
    },
    [markdown],
  );

  useEffect(() => {
    if (!editor) return;
    const current = getSpeakerMarkdownFromEditor(editor);
    if (current === markdown) return;
    editor.commands.setContent(markdown || "", false);
  }, [editor, markdown]);

  if (!markdown.trim()) {
    return <p className="speaker-md-empty muted">Nessun testo da mostrare.</p>;
  }

  return (
    <div className={className ? `speaker-md-editor ${className}` : "speaker-md-editor"}>
      <EditorContent editor={editor} />
    </div>
  );
}
